import React, { useState, useRef, useEffect } from "react";
import { Copy, MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import useToast from "~/components/ui/toast";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";
import { ShimmerText } from "./ShimmerText";
import { Conversation } from "./types";

interface ConversationItemProps {
  conversation: Conversation;
  isSelected: boolean;
  showCheckmark?: boolean;
  /** Whether this conversation has an active stream */
  isStreaming?: boolean;
  onSelect: (conversationId: string) => void;
  onDelete: (conversationId: string, event: React.MouseEvent) => void;
  onRename: (conversationId: string, displayName: string) => void;
  formatLabel: (conversation: Conversation) => string;
}

export function ConversationItem({
  conversation,
  isSelected,
  showCheckmark = false,
  isStreaming = false,
  onSelect,
  onDelete,
  onRename,
  formatLabel,
}: ConversationItemProps) {
  const { showSuccess } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [editingName, setEditingName] = useState("");
  const [popoverOpen, setPopoverOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const label = formatLabel(conversation);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSelect = () => {
    if (!isEditing) {
      onSelect(conversation.id);
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    onDelete(conversation.id, e);
  };

  const handleCopy = async (conversationId: string) => {
    try {
      await navigator.clipboard.writeText(
        new URL(`/admin/conversations/${conversationId}`, window.location.href).toString()
      );
      showSuccess("URL copied to clipboard");
    } catch (err) {
      console.error("Failed to copy URL:", err);
    }
  };

  const handleRenameClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingName(label);
    setPopoverOpen(false);
    setIsEditing(true);
  };

  const handleSave = () => {
    const trimmedName = editingName.trim();
    if (trimmedName && trimmedName !== label) {
      onRename(conversation.id, trimmedName);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave();
    } else if (e.key === "Escape") {
      e.preventDefault();
      setIsEditing(false);
    }
  };

  const CheckmarkIcon = () => (
    <svg className="size-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );

  return (
    <div
      className={cn(
        "relative group flex items-center justify-between px-2 py-1.5 mb-0.5 text-[13px] cursor-pointer rounded",
        isSelected && !showCheckmark ? "bg-gray-200/70 hover:bg-gray-200/70" : "hover:bg-gray-100",
        isSelected && showCheckmark && "bg-transparent"
      )}
      onClick={handleSelect}
      aria-label="Select conversation"
    >
      <div className="flex items-center overflow-hidden flex-1 min-w-0">
        {showCheckmark && (
          <div className="mr-2 w-4 flex justify-center shrink-0">
            {isSelected ? <CheckmarkIcon /> : null}
          </div>
        )}
        {isEditing ? (
          <input
            ref={inputRef}
            type="text"
            value={editingName}
            onChange={(e) => setEditingName(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            onClick={(e) => e.stopPropagation()}
            className="w-full bg-white border border-gray-300 rounded px-1 py-0.5 text-[13px] outline-none focus:border-blue-500"
          />
        ) : isStreaming && !isSelected ? (
          <ShimmerText className="truncate">{label}</ShimmerText>
        ) : (
          <Tooltip delayDuration={900}>
            <TooltipTrigger asChild>
              <p className="truncate">{label}</p>
            </TooltipTrigger>
            <TooltipContent hideArrow sideOffset={8}>
              <div>{label}</div>
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      {!isEditing && (
        <div className="flex items-center ml-2 shrink-0">
          <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
            <PopoverTrigger asChild>
              <button
                className="opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100 transition-opacity p-1 hover:bg-gray-200 data-[state=open]:bg-gray-200 rounded cursor-pointer"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreHorizontal className="size-4 text-gray-500" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-32 p-1" align="start">
              <button
                className="flex items-center gap-2 w-full px-2 py-1.5 text-sm rounded cursor-pointer hover:bg-gray-100"
                onClick={handleRenameClick}
              >
                <Pencil className="size-4" />
                Rename
              </button>
              <button
                className="flex items-center gap-2 w-full px-2 py-1.5 text-sm rounded cursor-pointer hover:bg-gray-100"
                onClick={(e) => {
                  e.stopPropagation();
                  setPopoverOpen(false);
                  handleCopy(conversation.id);
                }}
              >
                <Copy className="size-4" />
                Copy URL
              </button>
              <button
                className="flex items-center gap-2 w-full px-2 py-1.5 text-sm text-destructive hover:bg-destructive/10 rounded cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  setPopoverOpen(false);
                  handleDelete(e);
                }}
              >
                <Trash2 className="size-4" />
                Delete
              </button>
            </PopoverContent>
          </Popover>
        </div>
      )}
    </div>
  );
}
